import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AiSearchPatternCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AiSearchPatternCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  descriptionSelectedValue?: { displayId: string; id: string } | null;
  onDescriptionChange?: (value: string) => void;
  description: string;
  descriptionError?: string;
  patternSelectedValue?: { displayId: string; id: string } | null;
  onPatternChange?: (value: string) => void;
  pattern: string;
  patternError?: string;
  pathTypesSelectedValue?: { displayId: string; id: string } | null;
  onPathTypesChange?: (value: string[]) => void;
  pathTypes: string[];
  pathTypesError?: string;
  toolsSelectedValue?: { displayId: string; id: string } | null;
  onToolsChange?: (value: string[]) => void;
  tools: string[];
  toolsError?: string;
  effectivenessSelectedValue?: { displayId: string; id: string } | null;
  onEffectivenessChange?: (value: number | undefined) => void;
  effectiveness: number | undefined;
  effectivenessError?: string;
  evaluationCountSelectedValue?: { displayId: string; id: string } | null;
  onEvaluationCountChange?: (value: number) => void;
  evaluationCount: number;
  evaluationCountError?: string;
  successCountSelectedValue?: { displayId: string; id: string } | null;
  onSuccessCountChange?: (value: number) => void;
  successCount: number;
  successCountError?: string;
  sourceEvalIdsSelectedValue?: { displayId: string; id: string } | null;
  onSourceEvalIdsChange?: (value: string[]) => void;
  sourceEvalIds: string[];
  sourceEvalIdsError?: string;
  proposedBySelectedValue?: { displayId: string; id: string } | null;
  onProposedByChange?: (value: string) => void;
  proposedBy: string;
  proposedByError?: string;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  active: boolean;
  activeError?: string;
  prioritySelectedValue?: { displayId: string; id: string } | null;
  onPriorityChange?: (value: number) => void;
  priority: number;
  priorityError?: string;
  aiSearchPatternCreateStore: AiSearchPatternCreateStore;
};

export const AiSearchPatternCreateForm: React.FC<AiSearchPatternCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      description = "",
      descriptionError,
      onDescriptionChange = () => {},
      pattern = "",
      patternError,
      onPatternChange = () => {},
      pathTypes = [],
      pathTypesError,
      onPathTypesChange = () => {},
      tools = [],
      toolsError,
      onToolsChange = () => {},
      effectiveness = undefined,
      effectivenessError,
      onEffectivenessChange = () => {},
      evaluationCount = 0,
      evaluationCountError,
      onEvaluationCountChange = () => {},
      successCount = 0,
      successCountError,
      onSuccessCountChange = () => {},
      sourceEvalIds = [],
      sourceEvalIdsError,
      onSourceEvalIdsChange = () => {},
      proposedBy = "",
      proposedByError,
      onProposedByChange = () => {},
      active = false,
      activeError,
      onActiveChange = () => {},
      priority = 0,
      priorityError,
      onPriorityChange = () => {},
      aiSearchPatternCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AiSearchPattern"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "description":
                    onDescriptionChange?.(valor);
                    break;
                  case "pattern":
                    onPatternChange?.(valor);
                    break;
                  case "pathTypes":
                    onPathTypesChange?.(valor);
                    break;
                  case "tools":
                    onToolsChange?.(valor);
                    break;
                  case "effectiveness":
                    onEffectivenessChange?.(valor);
                    break;
                  case "evaluationCount":
                    onEvaluationCountChange?.(valor);
                    break;
                  case "successCount":
                    onSuccessCountChange?.(valor);
                    break;
                  case "sourceEvalIds":
                    onSourceEvalIdsChange?.(valor);
                    break;
                  case "proposedBy":
                    onProposedByChange?.(valor);
                    break;
                  case "active":
                    onActiveChange?.(valor);
                    break;
                  case "priority":
                    onPriorityChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={aiSearchPatternCreateStore.getCardErrors("null")}
              title={langStore.groupAiSearchPatternLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(aiSearchPatternCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    aiSearchPatternCreateStore.warnings["name"]?.message
                  }
                  label={langStore.AiSearchPatternNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.AiSearchPatternNameDescription}
                />

                <InputText
                  value={description}
                  error={Boolean(descriptionError)}
                  warning={
                    !descriptionError &&
                    Boolean(aiSearchPatternCreateStore.warnings["description"])
                  }
                  helperText={
                    descriptionError ||
                    aiSearchPatternCreateStore.warnings["description"]?.message
                  }
                  label={langStore.AiSearchPatternDescriptionLabel}
                  onChange={(value: string) => onDescriptionChange(value)}
                />

                <InputText
                  value={pattern}
                  error={Boolean(patternError)}
                  warning={
                    !patternError &&
                    Boolean(aiSearchPatternCreateStore.warnings["pattern"])
                  }
                  helperText={
                    patternError ||
                    aiSearchPatternCreateStore.warnings["pattern"]?.message
                  }
                  label={langStore.AiSearchPatternPatternLabel}
                  onChange={(value: string) => onPatternChange(value)}
                  description={langStore.AiSearchPatternPatternDescription}
                />
                <InputStringList
                  value={pathTypes}
                  error={Boolean(pathTypesError)}
                  helperText={
                    pathTypesError ||
                    aiSearchPatternCreateStore.warnings["pathTypes"]?.message
                  }
                  label={langStore.AiSearchPatternPathTypesLabel}
                  description={langStore.AiSearchPatternPathTypesDescription}
                  onChange={(value: string[]) => onPathTypesChange(value)}
                />
                <InputStringList
                  value={tools}
                  error={Boolean(toolsError)}
                  helperText={
                    toolsError ||
                    aiSearchPatternCreateStore.warnings["tools"]?.message
                  }
                  label={langStore.AiSearchPatternToolsLabel}
                  description={langStore.AiSearchPatternToolsDescription}
                  onChange={(value: string[]) => onToolsChange(value)}
                />
                <InputNumber
                  value={effectiveness}
                  nullable={true}
                  error={Boolean(effectivenessError)}
                  warning={
                    !effectivenessError &&
                    Boolean(
                      aiSearchPatternCreateStore.warnings["effectiveness"]
                    )
                  }
                  helperText={
                    effectivenessError ||
                    aiSearchPatternCreateStore.warnings["effectiveness"]
                      ?.message
                  }
                  allowNegative={true}
                  label={langStore.AiSearchPatternEffectivenessLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  onChange={(value: number | undefined) =>
                    onEffectivenessChange(value)
                  }
                />
                <InputNumber
                  value={evaluationCount}
                  error={Boolean(evaluationCountError)}
                  warning={
                    !evaluationCountError &&
                    Boolean(
                      aiSearchPatternCreateStore.warnings["evaluationCount"]
                    )
                  }
                  helperText={
                    evaluationCountError ||
                    aiSearchPatternCreateStore.warnings["evaluationCount"]
                      ?.message
                  }
                  allowNegative={false}
                  label={langStore.AiSearchPatternEvaluationCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onEvaluationCountChange(value)}
                />
                <InputNumber
                  value={successCount}
                  error={Boolean(successCountError)}
                  warning={
                    !successCountError &&
                    Boolean(aiSearchPatternCreateStore.warnings["successCount"])
                  }
                  helperText={
                    successCountError ||
                    aiSearchPatternCreateStore.warnings["successCount"]?.message
                  }
                  allowNegative={false}
                  label={langStore.AiSearchPatternSuccessCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onSuccessCountChange(value)}
                />
                <InputStringList
                  value={sourceEvalIds}
                  error={Boolean(sourceEvalIdsError)}
                  helperText={
                    sourceEvalIdsError ||
                    aiSearchPatternCreateStore.warnings["sourceEvalIds"]
                      ?.message
                  }
                  label={langStore.AiSearchPatternSourceEvalIdsLabel}
                  onChange={(value: string[]) => onSourceEvalIdsChange(value)}
                />

                <InputString
                  value={proposedBy}
                  error={Boolean(proposedByError)}
                  warning={
                    !proposedByError &&
                    Boolean(aiSearchPatternCreateStore.warnings["proposedBy"])
                  }
                  helperText={
                    proposedByError ||
                    aiSearchPatternCreateStore.warnings["proposedBy"]?.message
                  }
                  label={langStore.AiSearchPatternProposedByLabel}
                  onChange={(value: string) => onProposedByChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputBoolean
                  value={active}
                  error={Boolean(activeError)}
                  helperText={
                    activeError ||
                    aiSearchPatternCreateStore.warnings["active"]?.message
                  }
                  label={langStore.AiSearchPatternActiveLabel}
                  onChange={(value: boolean) => onActiveChange(value)}
                />
                <InputNumber
                  value={priority}
                  error={Boolean(priorityError)}
                  warning={
                    !priorityError &&
                    Boolean(aiSearchPatternCreateStore.warnings["priority"])
                  }
                  helperText={
                    priorityError ||
                    aiSearchPatternCreateStore.warnings["priority"]?.message
                  }
                  allowNegative={false}
                  label={langStore.AiSearchPatternPriorityLabel}
                  decimalScale={0}
                  onChange={(value: number) => onPriorityChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
